import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase, UserProfile } from '@/integrations/supabase/client'
import { toast } from 'sonner'

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  session: Session | null
  loading: boolean
  signUp: (email: string, password: string, userData: Partial<UserProfile>) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  // Fetch user profile from database
  const fetchUserProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('Error fetching user profile:', error)
        return null
      }

      return data as UserProfile
    } catch (error) {
      console.error('Error fetching user profile:', error)
      return null
    }
  }

  // Create user profile after signup
  const createUserProfile = async (userId: string, userData: Partial<UserProfile>): Promise<void> => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .insert([{
          id: userId,
          user_type: userData.user_type || 'regular',
          display_name: userData.display_name || '',
          business_name: userData.business_name || null,
          bio: userData.bio || null,
          location: userData.location || null,
          phone: userData.phone || null,
          website: userData.website || null,
          profile_image_url: userData.profile_image_url || null,
          is_verified: false
        }])

      if (error) {
        console.error('Error creating user profile:', error)
        throw error
      }
    } catch (error) {
      console.error('Error creating user profile:', error)
      throw error
    }
  }

  // Sign up new user
  const signUp = async (email: string, password: string, userData: Partial<UserProfile>): Promise<void> => {
    try {
      setLoading(true)
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      })

      if (error) {
        toast.error(error.message)
        throw error
      }

      if (data.user) {
        // Create user profile
        await createUserProfile(data.user.id, userData)
        
        toast.success('Account aangemaakt! Controleer je email voor verificatie.')
      }
    } catch (error) {
      console.error('Error signing up:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Sign in existing user
  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true)
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        toast.error(error.message)
        throw error
      }

      if (data.user) {
        const profile = await fetchUserProfile(data.user.id)
        setUserProfile(profile)
        toast.success('Welkom terug!')
      }
    } catch (error) {
      console.error('Error signing in:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Sign out user
  const signOut = async (): Promise<void> => {
    try {
      setLoading(true)
      
      const { error } = await supabase.auth.signOut()
      
      if (error) {
        toast.error(error.message)
        throw error
      }

      setUser(null)
      setUserProfile(null)
      setSession(null)
      
      toast.success('Je bent uitgelogd')
    } catch (error) {
      console.error('Error signing out:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Update user profile
  const updateProfile = async (updates: Partial<UserProfile>): Promise<void> => {
    try {
      if (!user) throw new Error('No user logged in')
      
      setLoading(true)
      
      const { error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)

      if (error) {
        toast.error(error.message)
        throw error
      }

      // Refresh profile
      const profile = await fetchUserProfile(user.id)
      setUserProfile(profile)
      
      toast.success('Profiel bijgewerkt!')
    } catch (error) {
      console.error('Error updating profile:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Refresh user profile
  const refreshProfile = async (): Promise<void> => {
    try {
      if (!user) return
      
      const profile = await fetchUserProfile(user.id)
      setUserProfile(profile)
    } catch (error) {
      console.error('Error refreshing profile:', error)
    }
  }

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Get initial session
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('Error getting session:', error)
          return
        }

        if (session) {
          setSession(session)
          setUser(session.user)
          
          // Fetch user profile
          const profile = await fetchUserProfile(session.user.id)
          setUserProfile(profile)
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session)
        
        setSession(session)
        setUser(session?.user || null)

        if (session?.user) {
          // Fetch user profile
          const profile = await fetchUserProfile(session.user.id)
          setUserProfile(profile)
        } else {
          setUserProfile(null)
        }
        
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const value = {
    user,
    userProfile,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    refreshProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}